import React, { ButtonHTMLAttributes, HTMLAttributes, LabelHTMLAttributes, ReactNode } from 'react';

import { IconX } from '@tabler/icons-react';
import { Button } from 'baseui/button';
import clsx from 'clsx';
import { Option } from 'features/common-elements';
import { Form, useField, useFormikContext } from 'formik';
import { MultiValue, SingleValue } from 'react-select';

import closeIcon from '../../assets/svgs/close-gray.svg';
import { Dropdown } from '../Dropdown';
import { UiInput, UiInputProps } from '../Input';
import { ErrorMessage } from './ErrorMessage';

const isMultiValue = (
  value: SingleValue<Option> | MultiValue<Option>,
): value is MultiValue<Option> => {
  return Array.isArray(value);
};

const CloseButton = ({ className, style, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={clsx(
      'bg-transparent absolute right-4 top-2.5 h-6 w-6 cursor-pointer border-none bg-cover',
      className,
    )}
    style={{ backgroundImage: `url('${closeIcon}')`, ...style }}
    {...props}
  />
);

interface SubmitButtonProps {
  label: string;
}

const SubmitButton: React.FC<SubmitButtonProps> = ({ label }) => {
  return (
    <div className="my-5 flex flex-row-reverse">
      <Button size="compact" type="submit">
        {label}
      </Button>
    </div>
  );
};

export interface StandardFormProps {
  closeModal: () => void;
  children: ReactNode;
}

const Error = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx('col-start-2 mt-5px text-red', className)} {...props} />
);

const StandardForm: React.FC<StandardFormProps> = ({ closeModal, children }) => {
  return (
    <Form>
      <div className="relative rounded-lg border border-pink-500 bg-slate-950 px-6 py-2 text-sm text-slate-400">
        <IconX
          onClick={closeModal}
          className="t absolute right-[16px] top-[16px] h-5 w-5 cursor-pointer"
        />
        {children}
      </div>
    </Form>
  );
};

interface FormikDropdownFieldProps {
  name: string;
  options: Option[];
  isMulti?: boolean;
  defaultValue?: Option;
}

interface FormikTextInputProps extends Partial<UiInputProps> {
  name: string;
}

const Label = ({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={clsx('self-center', className)} {...props} />
);

const FormikTextInput = ({
  name,
  variant = 'primary',
  size = 'small',
  ...rest
}: FormikTextInputProps) => {
  const [field, meta] = useField(name);

  return (
    <div>
      <UiInput size={size} variant={variant} {...field} {...rest} />
      <ErrorMessage>{meta.touched && meta.error ? <div>{meta.error}</div> : null}</ErrorMessage>
    </div>
  );
};

const FormikDropdownField: React.FC<FormikDropdownFieldProps> = ({ name, options }) => {
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();

  // Find the selected option from the value in the field
  const selectedOption = Array.isArray(field.value)
    ? options.filter((option) => field.value.includes(option.value))
    : options.find((option) => option.value === field.value);

  return (
    <div>
      <Dropdown
        variant={'gray' as 'gray'}
        options={options}
        value={Array.isArray(selectedOption) ? selectedOption?.[0]?.label : selectedOption?.label}
        onValueChange={(option) => {
          const value = option?.value;
          setFieldValue(name, value || '');
          helpers.setTouched(true);
        }}
      />
      <ErrorMessage>{meta.touched && meta.error ? <div>{meta.error}</div> : null}</ErrorMessage>
    </div>
  );
};

export {
  StandardForm,
  CloseButton,
  Error,
  Label,
  FormikTextInput,
  FormikDropdownField,
  SubmitButton,
};
